import { useEffect, useState } from "react";
import { api, unwrapArray } from "@/lib/api";
import { SearchSelect } from "@/components/ui/search-select";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Send } from "lucide-react";
import type { ProductVariantRecord } from "@/types";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  conversationId: string;
  windowOpen: boolean;
  onSent: () => void;
}

/** Sends a product's photo (or the chosen shade's) with its specification card into the WhatsApp chat */
export function ShareProductDialog({ open, onOpenChange, conversationId, windowOpen, onSent }: Props) {
  const { success, error } = useToast();
  const [productId, setProductId] = useState("");
  const [productLabel, setProductLabel] = useState("");
  const [variants, setVariants] = useState<ProductVariantRecord[]>([]);
  const [variantId, setVariantId] = useState("");
  const [includePrice, setIncludePrice] = useState(false);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setProductId("");
    setProductLabel("");
    setVariants([]);
    setVariantId("");
    setIncludePrice(false);
    setNote("");
  }, [open]);

  const pick = (id: string, row: any) => {
    setProductId(id);
    setProductLabel(row?.name || "");
    setVariantId("");
    setVariants([]);
    if (!id) return;
    api
      .get(`/api/Product/Variants/${id}`)
      .then((r) => setVariants(unwrapArray<ProductVariantRecord>(r).filter((v) => v.status !== false)))
      .catch(() => setVariants([]));
  };

  const send = async () => {
    setSending(true);
    try {
      await api.post(`/api/WhatsApp/Conversations/${conversationId}/SendProduct`, {
        product_id: productId,
        variant_id: variantId || undefined,
        include_price: includePrice,
        note: note.trim() || undefined,
      });
      success("Product sent", "It is on its way to the buyer");
      onSent();
      onOpenChange(false);
    } catch (err: any) {
      error("Could not send", err.message || "Please try again");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !sending && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Share a product</DialogTitle>
          <DialogDescription>The photo goes with a card of its specifications. Cost and margin are never included.</DialogDescription>
        </DialogHeader>
        {!windowOpen && (
          <p className="text-xs rounded-md bg-amber-500/10 text-amber-800 dark:text-amber-300 px-3 py-2">
            More than 24 hours since the buyer's last message, so WhatsApp only allows an approved template now.
          </p>
        )}
        <div className="space-y-3 py-1">
          <div className="space-y-1.5">
            <Label>Product *</Label>
            <SearchSelect
              endpoint="/api/Product/List"
              extraParams={{ active: "true" }}
              value={productId}
              selectedLabel={productLabel}
              getLabel={(row) => `${row.name} (${row.code})`}
              placeholder="Search products"
              onChange={pick}
            />
          </div>
          {variants.length > 0 && (
            <div className="space-y-1.5">
              <Label>Shade</Label>
              <select value={variantId} onChange={(e) => setVariantId(e.target.value)} className="w-full h-10 rounded-lg border border-input bg-background px-3 text-sm">
                <option value="">-- No specific shade --</option>
                {variants.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name} ({v.code})
                  </option>
                ))}
              </select>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={includePrice} onChange={(e) => setIncludePrice(e.target.checked)} />
            Include the price (per unit, plus GST)
          </label>
          <div className="space-y-1.5">
            <Label>Add a note (optional)</Label>
            <Textarea rows={2} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Available in 7 days, MOQ 300 m" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
            Cancel
          </Button>
          <Button onClick={send} disabled={sending || !productId || !windowOpen}>
            {sending ? <Loader2 className="size-4 animate-spin mr-1.5" /> : <Send className="size-4 mr-1.5" />}
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
